import React from 'react';
import { Star, Check, Sparkles } from 'lucide-react';
import { resolveTrustSignal } from '../../utils/trustSignal';

// Ett tillit-signal for plass-kortet: slår sammen de tre kvalitetsspråkene (kursivt score-
// tall + ★-rating + verifisert-badge) til ÉN linje med fast presedens (menneske > AI > rating,
// se resolveTrustSignal). Ren presentasjon — label-strengene kommer ferdig-oversatt fra
// forelderen (SpotCard har allerede `t`), så komponenten er fri for i18n-kobling og testbar.

export interface TrustLineProps {
  humanVerified?: boolean;
  aiSuitable?: boolean;
  ratingAverage?: number | null;
  ratingCount?: number;
  /** Ferdig oversatt, f.eks. «Verifisert av Kari» eller «Verifisert». */
  humanLabel: string;
  /** Ferdig oversatt, f.eks. «AI-anbefalt». */
  aiLabel: string;
  className?: string;
}

const Rating: React.FC<{ average: number; count?: number; small?: boolean }> = ({ average, count, small }) => (
  <span className={`flex items-center gap-1 ${small ? 'font-bold' : 'font-black'} text-amber-400`}>
    <Star size={small ? 11 : 12} className="fill-amber-400" />
    <span className="tabular-nums">{average.toFixed(1)}</span>
    {count ? <span className="text-[var(--text-muted)] font-medium">({count})</span> : null}
  </span>
);

const TrustLine: React.FC<TrustLineProps> = ({
  humanVerified, aiSuitable, ratingAverage, ratingCount, humanLabel, aiLabel, className,
}) => {
  const tier = resolveTrustSignal({ humanVerified, aiSuitable, ratingAverage });
  const hasRating = typeof ratingAverage === 'number' && ratingAverage > 0;
  if (tier === 'none') return null;

  return (
    <div
      data-testid="trust-line"
      className={`flex items-center flex-wrap gap-x-2 gap-y-0.5 text-2xs leading-none ${className ?? ''}`}
    >
      {tier === 'human' && (
        <span className="flex items-center gap-1.5 font-black text-amber-500">
          <span className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center text-white flex-shrink-0">
            <Check size={10} strokeWidth={3.5} />
          </span>
          {humanLabel}
        </span>
      )}
      {tier === 'ai' && (
        <span className="flex items-center gap-1.5 font-black text-emerald-500">
          <Sparkles size={12} className="fill-emerald-500/20 flex-shrink-0" />
          {aiLabel}
        </span>
      )}
      {tier === 'rated' && hasRating && <Rating average={ratingAverage!} count={ratingCount} />}
      {/* Når det høyeste signalet er menneske/AI, henges rating på som sekundær. */}
      {tier !== 'rated' && hasRating && (
        <>
          <span className="text-[var(--border-primary)]" aria-hidden>·</span>
          <Rating average={ratingAverage!} count={ratingCount} small />
        </>
      )}
    </div>
  );
};

export default TrustLine;
